/**
 * Job details found in the text of a contract. Nothing here is a fact about the job: each value
 * is a suggestion that quotes the contract line it came from, for the customer to confirm or
 * correct before the job is added. Dates are deliberately not suggested: a notice deadline runs
 * from the day work or materials were first furnished, which a contract's start date is not.
 */
export type Suggestion = { value: string; quote: string };
export type ContractFields = 'reference' | 'name' | 'addressLine1' | 'city' | 'stateCode' | 'postalCode' | 'county' | 'classification'
  | 'claimantRole' | 'contractingTier' | 'workScope' | 'contractAmount';
export type ContractSuggestions = { fields: Partial<Record<ContractFields, Suggestion>>; notes: string[] };

const clip = (text: string, length: number) => text.length > length ? `${text.slice(0, length - 1).trimEnd()}…` : text;
const tidy = (text: string) => text.replace(/\s+/g, ' ').trim();
const escape = (label: string) => label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/ /g, '\\s+');

/** The text after "Label:" on the first line that has one of the labels. */
function labeled(text: string, labels: string[]): { value: string; quote: string; rest: string } | undefined {
  const pattern = new RegExp(`(?:^|\\n)[ \\t]*(?:${labels.map(escape).join('|')})[ \\t]*(?::|#|\\u2013|\\u2014|-)[ \\t]*([^\\n]{2,300})([\\s\\S]*)`, 'i');
  const match = pattern.exec(text);
  if (!match) return undefined;
  return { value: tidy(match[1]!), quote: clip(tidy(match[0]!.split('\n').filter(Boolean)[0]!), 160), rest: match[2] ?? '' };
}

const STATE = '(A[KLRZ]|C[AOT]|D[CE]|FL|GA|HI|I[ADLN]|K[SY]|LA|M[ADEINOST]|N[CDEHJMVY]|O[HKR]|PA|RI|S[CD]|T[NX]|UT|V[AT]|W[AIVY])';
const CITY_STATE_ZIP = new RegExp(`([A-Za-z][A-Za-z .'-]{1,40}?),?\\s+${STATE}\\.?,?\\s+(\\d{5})(?:-\\d{4})?\\b`);

function address(text: string): Partial<Record<ContractFields, Suggestion>> {
  const found = labeled(text, ['project address', 'project location', 'job site address', 'job site', 'jobsite', 'site address',
    'property address', 'premises', 'location of work', 'location of the work', 'project site']);
  if (!found) return {};
  // "123 Main St, Tampa, FL 33602" on one line, or the street on this line and city, state and ZIP on the next.
  const lines = [found.value, tidy(found.rest.split('\n').find(line => line.trim()) ?? '')];
  const joined = tidy(`${lines[0]}, ${lines[1]}`);
  const full = CITY_STATE_ZIP.exec(lines[0]!) ?? CITY_STATE_ZIP.exec(joined);
  if (!full) return {};
  const street = CITY_STATE_ZIP.test(lines[0]!) ? tidy(lines[0]!.slice(0, full.index).replace(/[,\s]+$/, '')) : lines[0]!.replace(/[,\s]+$/, '');
  const quote = clip(CITY_STATE_ZIP.test(lines[0]!) ? found.quote : `${found.quote} ${lines[1]}`, 200);
  if (!/\d/.test(street)) return {};
  return {
    addressLine1: { value: street, quote }, city: { value: tidy(full[1]!), quote },
    stateCode: { value: full[2]!, quote }, postalCode: { value: full[3]!, quote },
  };
}

function money(text: string): Suggestion | undefined {
  const match = /(contract\s+(?:price|sum|amount)|subcontract\s+(?:price|sum|amount)|lump\s+sum|total\s+(?:contract\s+)?price)[^$\n]{0,60}\$\s?([\d,]+(?:\.\d{1,2})?)/i.exec(text);
  if (!match) return undefined;
  const value = match[2]!.replace(/,/g, '');
  return Number.isFinite(Number(value)) ? { value: Number(value).toFixed(2), quote: clip(tidy(match[0]!), 160) } : undefined;
}

function role(text: string): Partial<Record<ContractFields, Suggestion>> {
  const head = text.slice(0, 1500);
  // The contract line the words are on.
  const quote = (match: RegExpExecArray) => {
    const start = head.lastIndexOf('\n', match.index) + 1;
    const end = head.indexOf('\n', match.index + match[0].length);
    return { quote: clip(tidy(head.slice(start, end < 0 ? undefined : end)), 160) };
  };
  let match: RegExpExecArray | null;
  // A subcontract is made with the general contractor; whether that contractor is itself a
  // subcontractor is not in the agreement, so the customer confirms who hired them.
  if ((match = /\bsub-?contract(?:or)?\s+(?:agreement|price)\b|\bsubcontract\b/i.exec(head)))
    return { claimantRole: { value: 'SUBCONTRACTOR', ...quote(match) }, contractingTier: { value: '1', ...quote(match) } };
  if ((match = /\b(?:purchase\s+order|material\s+supply|supply\s+agreement|supplier)\b/i.exec(head)))
    return { claimantRole: { value: 'MATERIAL_SUPPLIER', ...quote(match) } };
  if ((match = /\b(?:equipment\s+(?:rental|lease)|rental\s+agreement)\b/i.exec(head)))
    return { claimantRole: { value: 'EQUIPMENT_LESSOR', ...quote(match) } };
  if ((match = /\bowner[\s-]+contractor\b|\bprime\s+contract\b|\bagreement\s+between\s+owner\s+and\s+contractor\b/i.exec(head)))
    return { claimantRole: { value: 'GENERAL_CONTRACTOR', ...quote(match) }, contractingTier: { value: '0', ...quote(match) } };
  return {};
}

function classification(text: string): Suggestion | undefined {
  let match = /\b(?:United\s+States(?:\s+of\s+America)?|General\s+Services\s+Administration|U\.S\.\s+(?:Army|Navy|Air\s+Force)|Army\s+Corps\s+of\s+Engineers|federal\s+government)\b/i.exec(text);
  if (match) return { value: 'FEDERAL', quote: clip(tidy(text.slice(Math.max(0, match.index - 40), match.index + match[0].length + 40)), 160) };
  // "City of Tampa" is a name, so its capitals count; "state of the art" is not.
  match = /\b(?:City|County|Town|Village|State|Commonwealth)\s+of\s+[A-Z][A-Za-z]+/.exec(text)
    ?? /\b(?:school\s+district|school\s+board|board\s+of\s+county\s+commissioners|housing\s+authority|water\s+authority|port\s+authority)\b/i.exec(text);
  return match ? { value: 'PUBLIC', quote: clip(tidy(text.slice(Math.max(0, match.index - 40), match.index + match[0].length + 40)), 160) } : undefined;
}

function scope(text: string): Suggestion | undefined {
  const match = /(?:^|\n)[ \t]*(?:\d+[.)]?\s*)?(?:scope\s+of\s+(?:the\s+)?(?:work|services)|description\s+of\s+(?:the\s+)?work)[ \t]*:?[ \t]*\n?([^\n][\s\S]{20,}?)(?:\n\s*\n|$)/i.exec(text);
  if (!match) return undefined;
  const paragraph = tidy(match[1]!);
  const sentence = /^(.{20,400}?[.;])(?:\s|$)/.exec(paragraph)?.[1] ?? clip(paragraph, 400);
  return { value: clip(sentence, 400), quote: clip(paragraph, 200) };
}

export function suggestFromContract(rawText: string): ContractSuggestions {
  const text = rawText.replace(/\r\n?/g, '\n').replace(/[ \t]+/g, ' ');
  const fields: ContractSuggestions['fields'] = { ...address(text), ...role(text) };
  const put = (key: ContractFields, found: { value: string; quote: string } | undefined) => { if (found && found.value) fields[key] = { value: found.value, quote: found.quote }; };
  put('reference', labeled(text, ['project no', 'project number', 'job no', 'job number', 'contract no', 'contract number', 'subcontract no',
    'subcontract number', 'agreement no', 'purchase order no', 'purchase order number', 'po number']));
  put('name', labeled(text, ['project name', 'project title', 'job name', 'project']));
  put('contractAmount', money(text));
  put('classification', classification(text));
  put('workScope', scope(text));
  const county = /\b(?:in|of|for)\s+([A-Z][A-Za-z.'-]+(?:\s[A-Z][A-Za-z.'-]+)?)\s+County\b/.exec(text);
  if (county && !/^(?:The|This|Said|Such|Each|Any|Same)$/.test(county[1]!))
    fields.county = { value: county[1]!, quote: clip(tidy(text.slice(Math.max(0, county.index - 20), county.index + county[0].length + 30)), 160) };
  const notes: string[] = [];
  const start = /(?:commencement|start(?:ing)?|mobilization)\s+date[^\n:]{0,20}:?\s*([A-Z][a-z]+\.?\s+\d{1,2},?\s+\d{4}|\d{1,2}\/\d{1,2}\/\d{2,4})/i.exec(text);
  if (start) notes.push(`Your contract gives a start date of ${start[1]}. Enter the day you actually first supplied work or materials as the first day below; the notice deadline runs from that day.`);
  return { fields, notes };
}

/** The suggestions as the job form's starting values, in the form's own terms. */
export function suggestedJobValues({ fields }: ContractSuggestions) {
  const value = (key: ContractFields) => fields[key]?.value;
  return {
    ...(value('reference') ? { reference: value('reference')! } : {}), ...(value('name') ? { name: value('name')! } : {}),
    ...(value('addressLine1') ? { addressLine1: value('addressLine1')! } : {}), ...(value('city') ? { city: value('city')! } : {}),
    ...(value('county') ? { county: value('county')! } : {}), ...(value('stateCode') ? { stateCode: value('stateCode')! } : {}),
    ...(value('postalCode') ? { postalCode: value('postalCode')! } : {}), ...(value('classification') ? { classification: value('classification')! } : {}),
    ...(value('claimantRole') ? { claimantRole: value('claimantRole')! } : {}), ...(value('contractingTier') ? { contractingTier: Number(value('contractingTier')) } : {}),
    ...(value('workScope') ? { workScope: value('workScope')! } : {}),
    ...(value('contractAmount') ? { contractAmountCents: String(Math.round(Number(value('contractAmount')) * 100)) } : {}),
  };
}

/** Plain-language names for the fields, in the order the form shows them. */
export const contractFieldLabels: Array<[ContractFields, string]> = [
  ['reference', 'Job reference'], ['name', 'Job name'], ['addressLine1', 'Address'], ['city', 'City'], ['county', 'County'], ['stateCode', 'State'],
  ['postalCode', 'Postal code'], ['classification', 'Type of job'], ['claimantRole', 'Your role'], ['contractingTier', 'Who hired you'],
  ['workScope', 'Your work'], ['contractAmount', 'Contract amount'],
];
export const contractValueLabels: Record<string, Record<string, string>> = {
  classification: { PRIVATE: 'Private property', PUBLIC: 'State or local government', FEDERAL: 'Federal government' },
  claimantRole: { SUBCONTRACTOR: 'Subcontractor', GENERAL_CONTRACTOR: 'General contractor', MATERIAL_SUPPLIER: 'Material supplier', EQUIPMENT_LESSOR: 'Equipment rental company' },
  contractingTier: { '0': 'The property owner', '1': 'The general contractor', '2': 'Another subcontractor', '3': 'Someone further down the hiring chain' },
};
